import React from 'react';
import { ButtonSelect, Button, Label, Help, Input, Icon, Text, Textarea, Block } from '@upsales/components';
import './CreateGroupMail.scss';
import bemClass from '@upsales/components/Utils/bemClass';
import getAngularModule from '../../angularHelpers/getAngularModule';
import UpSelect from 'Components/Inputs/UpSelect';
import MailCampaign, { status } from 'App/resources/MailCampaign';
import history from 'App/pages/routes/history';
import GroupMailEditorRobot from '../GroupMailEditor/GroupMailEditorRobot';
import FeatureIntroModal from 'Components/FeatureIntroModal';
import GroupMailAttr from 'Attributes/MailCampaignAttributes';
import AI from 'App/resources/AI';
import EventBus from 'App/services/EventBus';
import AcceptAISuggestionContract from 'App/components/AcceptAISuggestionContract';
import { AITracker } from 'App/babel/helpers/Tracker';
import logError from 'Helpers/logError';
import { getStyleDataAndGenerateHTML } from 'App/helpers/mailTemplateHelpers';
import MailTemplate from 'App/resources/Model/MailTemplate';
import Segment from 'App/resources/Model/Segment';
import Project from 'App/resources/Model/Project';
import { ListViewFilter } from 'App/resources/AllIWant';
import GroupMailCategory, { type GroupMailCategoryType } from 'App/resources/GroupMailCategory';
import t from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import moment from 'moment';
import { SetupComponentProps, setupComponentCompatibility } from 'App/helpers/angularPortingHelpers';
import { parseFilters } from 'App/helpers/filterHelper';
import { getConfig } from 'App/helpers/filterHelperInstances';

const SUBJECT_LENGTH: number = GroupMailAttr.subject.size!;

interface ModalParams {
	socialEventId?: number;
	segment?: Segment;
	project?: Project;
	copy?: any;
	filters?: { [filterName: string]: ListViewFilter };
}

type Props = ModalParams & SetupComponentProps;

type State = {
	aiMode: boolean;
	showAISuggestions: boolean;
	saving: boolean;
	loading: boolean;
	categories: GroupMailCategoryType[];
	category: GroupMailCategoryType | null;
	internalName: string;
	aiPrompt: string;
	expanded: boolean;
};

interface ILang {
	headline: string;
	description: string;
	internalName: string;
	whatTypeOfMailIsThis: string;
	whatIsAnEmailType: string;
	nextStep: string;
	abort: string;
	aiDescription1: string;
	aiDescription2: string;
	aiDescription3: string;
	generateAIContent: string;
	AIToggleAI: string;
	AIToggleSelf: string;
	AIheadline: string;
	AIdescription: string;
}

class CreateGroupMail extends React.PureComponent<Props, State> {
	lang: ILang;
	clientParamUnsubscribe: () => void;

	constructor(p: Props) {
		super(p);
		GroupMailCategory.find({ active: 1 })
			.then(res => {
				this.setState({ categories: res.data, loading: false, category: res.data[0] });
			})
			.catch(error => {
				logError(error, 'Error fetching group mail');
			});

		this.onCategorySelect = this.onCategorySelect.bind(this);
		this.onSave = this.onSave.bind(this);
		this.onNameChange = this.onNameChange.bind(this);

		this.lang = {
			headline: t('groupMail.letsSendEmails'),
			description: t('groupMail.firstThingIsName'),
			internalName: t('groupMail.internalEmailCampaignName'),
			whatTypeOfMailIsThis: t('groupMail.whatTypeOfMailIsThis'),
			whatIsAnEmailType: t('groupMail.whatIsAnEmailType'),
			nextStep: t('groupMail.nextTemplateDesign'),
			abort: t('default.abort'),
			aiDescription1: t('groupMail.aiDescription1'),
			aiDescription2: t('groupMail.aiDescription2'),
			aiDescription3: t('groupMail.aiDescription3'),
			generateAIContent: t('groupMail.generateAIContent'),
			AIToggleAI: t('groupMail.AIToggle.AI'),
			AIToggleSelf: t('groupMail.AIToggle.self'),
			AIheadline: t('groupMail.AIheadline'),
			AIdescription: t('groupMail.AIdescription')
		};

		const haveEnabledAISuggestions = AppService.getMetadata().params.AISuggestionsEnabled;
		const showAISuggestions = haveEnabledAISuggestions;

		this.state = {
			internalName: '',
			category: null,
			loading: true,
			saving: false,
			aiMode: false,
			showAISuggestions,
			aiPrompt: '',
			expanded: false,
			categories: []
		};

		if (p.copy) {
			this.setState({ internalName: `${p.copy.name || ''} (${t('default.copy')})` });
		}

		this.clientParamUnsubscribe = EventBus.on('clientParam.updated', (event, clientParam) => {
			if (clientParam?.paramId === 244) {
				this.setState({
					showAISuggestions: clientParam.value,
					aiMode: clientParam.value
				});
			}
		});
	}

	componentWillUnmount() {
		this.clientParamUnsubscribe?.();
	}

	onCategorySelect({ target }: { target: { added: GroupMailCategoryType } }) {
		this.setState({ category: target.added });
	}

	onNameChange({ target: { value } }: { target: { value: string } }) {
		this.setState({ internalName: value });
	}

	async onSaveAI() {
		try {
			AITracker.track(AITracker.events.GENERATE_MAIL_TEMPLATE, {});

			const StandardMailTemplate = getAngularModule('StandardMailTemplate');

			const [{ data: templates }, { data: ai }] = await Promise.all([
				StandardMailTemplate.find({}),
				AI.find({ prompt: this.state.aiPrompt, task: 'generateMailCampaign' })
			]);

			const aiAnswer = ai[0];

			const template = templates.find((template: MailTemplate) => template.id === 25);
			let bodyJson = (template.bodyJson.substr(0, 2947) + template.bodyJson.substr(11738)).replace(
				'\n\t\t\t\t\t\t\t\t\t\t<h2 class="text-left">Sociis natoque penatibus et magnis dis montes</h2>\n\t\t\t\t\t\t\t\t\t\t<p class="ingress text-left">Duis mollis, est non commodo luctus, nisi erat <strong>porttitor ligula</strong>, eget lacinia odio sem nec elit. Donec ullamcorper nulla non metus.</p>\n\t\t\t\t\t\t\t\t\t\t<ul>\n\t\t\t\t\t\t\t\t\t\t\t<li class="text-left">Donec sed odio dui. Donec ullamcorper nulla non metus auctor fringilla.</li>\n\t\t\t\t\t\t\t\t\t\t\t<li class="text-left">Vulla vitae elit libero, a pharetra augue</li>\n\t\t\t\t\t\t\t\t\t\t\t<li class="text-left">Cras mattis <a href="#">consectetur purus sit</a> amet fermentum.</li>\n\t\t\t\t\t\t\t\t\t\t\t<li class="text-left">Donec sed odio dui.</li>\n\t\t\t\t\t\t\t\t\t\t</ul>\n\t\t\t\t\t\t\t\t\t\t<p class="text-left"><strong>Integer posuere erat a ante</strong> venenatis dapibus posuere velit aliquet. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.</p>\n\t\t\t\t\t\t\t\t\t',
				aiAnswer.content
			);

			if (aiAnswer.imgUrl) {
				bodyJson = bodyJson.replace(
					'https://img.upsales.com/upsales/ad-templates/beach_cliffs_580.jpg',
					aiAnswer.imgUrl
				);
			}

			const body = await getStyleDataAndGenerateHTML(bodyJson);

			const overrides = {
				name: 'AI generated marketing mail ' + moment().format('YYYY-MM-DD'),
				subject: aiAnswer.subject,
				category: this.state.category,
				isDraft: true,
				body,
				bodyJson,
				template: { id: template.id }
			};

			const saveData = {
				...MailCampaign.new(),
				...overrides
			};

			if (this.props.socialEventId) {
				saveData.socialEventId = this.props.socialEventId;
				saveData.socialEventSendToStatus = 'invited';
			}

			MailCampaign.save(saveData)
				.then(res => {
					history.push('/group-mail-editor/' + res.data.id + '/design');
					setTimeout(() => this.props.resolve(res.data), 500);
				})
				.catch(() => this.setState({ saving: false }));
		} catch (error) {
			logError(error, 'failed to generate group mail');
			this.setState({ saving: false });
		}
	}

	onSave(e: React.ChangeEvent<HTMLFormElement>) {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ saving: true });

		if (this.state.aiMode) {
			this.onSaveAI();
			return;
		}

		const overrides = {
			name: this.state.internalName,
			category: this.state.category,
			isDraft: true
		};
		const self = AppService.getSelf();

		let saveData;
		if (this.props.copy) {
			saveData = {
				...this.props.copy,
				id: undefined,
				jobId: undefined,
				flowId: undefined,
				sendDate: undefined,
				socialEventId: undefined,
				socialEventSendToStatus: null,
				status: status.DRAFT,
				template: null,
				user: self,
				...overrides
			};
		} else {
			saveData = {
				...MailCampaign.new(),
				subject: this.state.internalName,
				...overrides
			};
		}
		if (this.props.filters) {
			const rb = parseFilters(this.props.filters, 'contact', null, null, { getConfig });
			saveData.filter = JSON.stringify(rb.build());
			saveData.filterConfig = this.props.filters;
		} else if (this.props.segment) {
			saveData.segment = this.props.segment;
		} else if (this.props.project) {
			saveData.project = this.props.project;
		} else if (this.props.socialEventId) {
			saveData.socialEventId = this.props.socialEventId;
			saveData.socialEventSendToStatus = 'invited';
		}

		MailCampaign.save(saveData)
			.then(res => {
				history.push('/group-mail-editor/' + res.data.id);
				setTimeout(() => this.props.resolve(res.data), 500);
			})
			.catch(() => this.setState({ saving: false }));
	}

	render() {
		const classes = new bemClass('CreateGroupMail');
		const options = [
			{ value: true, title: this.lang.AIToggleAI },
			{ value: false, title: this.lang.AIToggleSelf }
		];

		const categorySelectOpts = {
			formatSelection: (category: GroupMailCategoryType, _container: any, escape: (value: string) => string) =>
				escape(category.title),
			formatResult: (category: any, _container: any, query: string, escape: (value: string) => string) =>
				escape(category.title),
			matcher: (term: string, _undef: any, item: GroupMailCategoryType) =>
				item.title.toLowerCase().indexOf(term.toLowerCase()) !== -1
		};

		const title = !this.state.showAISuggestions ? this.lang.headline : this.lang.AIheadline;
		const description = !this.state.showAISuggestions
			? this.lang.description
			: this.state.aiMode
			? ''
			: this.lang.headline;

		return (
			<FeatureIntroModal
				className={`${classes.b()} FullScreenModal`}
				title={title}
				description={description}
				loading={this.state.loading}
				onClose={this.props.reject}
				renderIllustration={this.state.aiMode ? null : () => <GroupMailEditorRobot state="booting" />}
			>
				<form onSubmit={this.onSave} autoComplete="off">
					{!this.state.showAISuggestions ? (
						<AcceptAISuggestionContract title="acceptAISuggestionContract.title" space="mbxl" />
					) : null}
					{this.state.showAISuggestions ? (
						<ButtonSelect
							options={options}
							onChange={value => this.setState({ aiMode: value })}
							value={this.state.aiMode}
						/>
					) : null}
					{this.state.showAISuggestions && this.state.aiMode ? (
						<>
							<Block space="mtxl">
								<Text>{this.lang.aiDescription1}</Text>
							</Block>
							<Block style={{ marginTop: 25 }}>
								<Text>{this.lang.aiDescription2}</Text>
							</Block>
							<Block space="mbxl">
								<Text italic={true}>{this.lang.aiDescription3}</Text>
							</Block>

							{/* Textarea should be updated to handle style changes when the maxlength is reached. */}
							<Textarea
								disabled={this.state.saving}
								value={this.state.aiPrompt}
								onChange={({ target: { value } }) => this.setState({ aiPrompt: value })}
								maxLength={1000}
							/>

							<Button
								style={{ maxWidth: 430 }}
								color={!this.state.aiPrompt ? 'grey' : 'green'}
								shadow={!this.state.aiPrompt ? 'none' : 'high'}
								size="xl"
								block
								className={classes.elem('next-step').b()}
								disabled={!this.state.aiPrompt || this.state.aiPrompt?.length > 1000}
								loading={this.state.saving}
								submit
							>
								<Icon space="mrl" name="play" />
								{this.lang.generateAIContent}
							</Button>
						</>
					) : (
						<>
							<Label required maxLength={SUBJECT_LENGTH} value={this.state.internalName}>
								{this.lang.internalName}
							</Label>
							<Input
								autoComplete="internal-name"
								disabled={this.state.saving}
								value={this.state.internalName}
								onChange={this.onNameChange}
								autofocus={true}
								state={this.state.internalName?.length > SUBJECT_LENGTH ? 'error' : undefined}
							/>

							<Label required>{this.lang.whatTypeOfMailIsThis}</Label>
							<UpSelect
								data={this.state.categories}
								defaultValue={this.state.category}
								options={categorySelectOpts}
								onChange={this.onCategorySelect}
								disabled={this.state.saving}
								required={true}
							/>

							<Help articleId={796} sidebar>
								<Button color="grey" type="link" className="btn-inline">
									{this.lang.whatIsAnEmailType}
								</Button>
							</Help>

							<Button
								color={!this.state.internalName || !this.state.category ? 'grey' : 'green'}
								shadow={!this.state.internalName || !this.state.category ? 'none' : 'high'}
								size="xl"
								block
								className={classes.elem('next-step').b()}
								disabled={
									!this.state.internalName ||
									!this.state.category ||
									this.state.internalName?.length > SUBJECT_LENGTH
								}
								loading={this.state.saving}
								submit
							>
								{this.lang.nextStep}
							</Button>

							<Button
								className={classes.elem('cancel').b()}
								color="grey"
								type="link"
								block
								size="lg"
								onClick={() => this.props.reject()}
								disabled={this.state.saving}
							>
								{this.lang.abort}
							</Button>
						</>
					)}
				</form>
			</FeatureIntroModal>
		);
	}
}

export const CreateGroupMailReact = setupComponentCompatibility<ModalParams>(CreateGroupMail, {});

export default CreateGroupMail;
